<template>
  <main
    class="flex flex-col w-full p-8 md:p-12 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen"
  >
    <div class="flex justify-between max-w-6xl mx-auto w-full mb-8">
      <RouterLink
        to="/"
        class="px-6 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-all duration-300 font-medium flex items-center space-x-2 text-sm shadow-md hover:shadow-lg"
      >
        <i class="fas fa-cog"></i>
        <span>Configurations</span>
      </RouterLink>
      <RouterLink
        to="/home"
        class="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-300 font-medium flex items-center space-x-2 text-sm shadow-md hover:shadow-lg"
      >
        <i class="fas fa-qrcode"></i>
        <span>Générer</span>
      </RouterLink>
    </div>

    <div class="max-w-6xl mx-auto w-full mt-8">
      <div class="text-center mb-8">
        <h1 class="text-4xl font-bold text-gray-800 mb-3">
          Generation Tracking
        </h1>
        <p class="">View your generation history</p>
      </div>

      <div class="space-y-6">
        <div
          v-for="generation in generations"
          :key="generation._id"
          class="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 transform transition-all duration-300 hover:shadow-2xl"
        >
          <div class="flex justify-between items-start mb-4">
            <div>
              <h2 class="text-xl font-semibold text-gray-800">
                {{ generation.campaignName }}
              </h2>
              <p class="text-gray-500 text-sm">
                Generated on
                {{ new Date(generation.createdAt).toLocaleDateString() }}
              </p>
            </div>
            <div class="text-right">
              <p class="text-sm text-gray-500">
                Quantity: {{ generation.quantity }}
              </p>
              <p class="text-sm text-gray-500">
                Owned:
                {{
                  generation.links.filter((link) => link.status === 'owned')
                    .length
                }}
                | Pending:
                {{
                  generation.links.filter((link) => link.status === 'pending')
                    .length
                }}
              </p>
              <div class="flex space-x-2 mt-2">
                <button
                  @click="regenerateZip(generation.links)"
                  class="px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-300 text-xs flex items-center space-x-1"
                >
                  <i class="fas fa-file-archive"></i>
                  <span>ZIP</span>
                </button>
                <button
                  @click="regenerateCsv(generation.links)"
                  class="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-300 text-xs flex items-center space-x-1"
                >
                  <i class="fas fa-file-csv"></i>
                  <span>CSV</span>
                </button>
                <button
                  @click="leaveGeneration(generation)"
                  class="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-300 text-xs flex items-center space-x-1"
                >
                  <i class="fas fa-sign-out-alt"></i>
                  <span>Leave</span>
                </button>
              </div>
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="min-w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    class="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Link
                  </th>
                  <th
                    class="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Preview
                  </th>
                  <th
                    class="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr
                  v-for="link in generation.links"
                  :key="link.link"
                  class="hover:bg-gray-50 transition-colors duration-200"
                >
                  <td class="px-4 py-3 whitespace-nowrap">
                    <a
                      :href="link.link"
                      target="_blank"
                      class="text-indigo-600 hover:text-indigo-800 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <i class="fas fa-external-link-alt"></i>
                      <span>{{ link.link }}</span>
                    </a>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <img
                      :src="generation.image"
                      alt="Badge Preview"
                      class="w-10 h-10 rounded-lg"
                    />
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <span
                      :class="
                        link.status === 'owned'
                          ? 'text-green-600'
                          : 'text-yellow-600'
                      "
                      class="px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {{ link.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { ref, onMounted, inject } from 'vue';
import { RouterLink } from 'vue-router';
import Cookies from 'js-cookie';

export default {
  setup() {
    const api = inject('api');
    const utils = inject('utils');
    const generations = ref([]);
    const loading = ref(false);

    const loadGenerations = async () => {
      loading.value = true;
      const savedKeys = Cookies.get('apiKeys');
      if (savedKeys) {
        const apiKeys = JSON.parse(savedKeys);
        for (const keyObj of apiKeys) {
          generations.value.push({
            ...keyObj.generations,
            image: keyObj.data.image,
          });
        }
      }
      loading.value = false;
    };

    const leaveGeneration = async (generation) => {
      try {
        await api.deleteGeneration(generation._id);
        await loadGenerations();
      } catch (error) {
        console.error('Error leaving generation:', error);
      }
    };

    const regenerateZip = async (links) => {
      try {
        const zipBlob = await utils.generateZip(links);
        utils.downloadFile(zipBlob, 'qr-codes.zip');
      } catch (error) {
        console.error('Error generating ZIP:', error);
      }
    };

    const regenerateCsv = async (links) => {
      try {
        const csvBlob = await utils.generateCsv(links);
        utils.downloadFile(csvBlob, 'liens.csv');
      } catch (error) {
        console.error('Error generating CSV:', error);
      }
    };

    onMounted(() => {
      loadGenerations().then(() => {
        console.log('generations', generations.value);
      });
    });

    return {
      generations,
      loading,
      regenerateZip,
      regenerateCsv,
      leaveGeneration,
    };
  },
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

.text-green-600 {
  background-color: rgba(16, 185, 129, 0.1);
  color: rgb(16, 185, 129);
}

.text-yellow-600 {
  background-color: rgba(202, 138, 4, 0.1);
  color: rgb(202, 138, 4);
}

.text-gray-600 {
  background-color: rgba(75, 85, 99, 0.1);
  color: rgb(75, 85, 99);
}
</style>
